import { useState } from "react";
import { Badge, Button, Card, EmptyState, MUTED_TEXT, Spinner, stackStyle } from "@dayvise/react-ui";
import { useSnapshotMutations, useSnapshots } from "./hooks.ts";

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KiB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MiB`;
}

/**
 * Snapshots cover agent configuration and private skills only. Memories,
 * files, conversations, Kanban state and running state are excluded by
 * design, and restoring replaces the current private configuration with an
 * explicit confirmation.
 */
export function SnapshotsPanel() {
  const query = useSnapshots();
  const mutations = useSnapshotMutations();
  const [confirmingId, setConfirmingId] = useState<string | null>(null);
  const snapshots = query.data?.snapshots ?? [];
  const error = query.error ?? mutations.create.error ?? mutations.restore.error ?? mutations.remove.error;

  return (
    <Card
      title="Configuration snapshots"
      subtitle="A snapshot records your private agents and skills as they are now. Restoring one replaces your current private configuration; memories, files and conversations are never touched."
    >
      <div style={stackStyle("var(--dayvise-space-sm)")}>
        {query.isPending ? <Spinner label="Loading snapshots" /> : null}
        {error ? <p role="alert" style={{ color: "var(--dayvise-color-danger)" }}>{error instanceof Error ? error.message : "Snapshot action failed"}</p> : null}
        <div>
          <Button
            onClick={() => mutations.create.mutate()}
            disabled={mutations.create.isPending}
          >
            {mutations.create.isPending ? "Creating snapshot…" : "Snapshot now"}
          </Button>
        </div>
        {mutations.create.data ? (
          <p style={{ ...MUTED_TEXT, margin: 0 }}>
            Snapshot {mutations.create.data.snapshot.snapshotId} saved ({formatBytes(mutations.create.data.snapshot.bytes)}).
          </p>
        ) : null}
        {!query.isPending && snapshots.length === 0 ? (
          <EmptyState title="No snapshots yet" description="Create one before changing agents or skills, so a known-good configuration can be restored." />
        ) : null}
        {snapshots.map((snapshot) => (
          <div key={snapshot.snapshotId} style={stackStyle("var(--dayvise-space-xs)")}>
            <div style={{ display: "flex", gap: "var(--dayvise-space-xs)", alignItems: "center", flexWrap: "wrap" }}>
              <Badge tone="neutral">{snapshot.createdAt.slice(0, 10)}</Badge>
              <span>
                {snapshot.agents} {snapshot.agents === 1 ? "agent" : "agents"} · {snapshot.skills} {snapshot.skills === 1 ? "skill" : "skills"} · {formatBytes(snapshot.bytes)}
              </span>
            </div>
            <p style={{ ...MUTED_TEXT, margin: 0 }}>
              Agents: {snapshot.agentNames.length > 0 ? snapshot.agentNames.join(", ") : "none"} · Skills: {snapshot.skillNames.length > 0 ? snapshot.skillNames.join(", ") : "none"}
            </p>
            {confirmingId === snapshot.snapshotId ? (
              <div style={stackStyle("var(--dayvise-space-xs)")}>
                <p style={{ margin: 0, color: "var(--dayvise-color-warning)" }}>
                  Restoring replaces your private agent configuration and private skills with this snapshot. Continue?
                </p>
                <div style={{ display: "flex", gap: "var(--dayvise-space-xs)" }}>
                  <Button
                    variant="danger"
                    disabled={mutations.restore.isPending}
                    onClick={() => {
                      mutations.restore.mutate(snapshot.snapshotId, { onSuccess: () => setConfirmingId(null) });
                    }}
                  >
                    {mutations.restore.isPending ? "Restoring…" : "Yes, restore"}
                  </Button>
                  <Button onClick={() => setConfirmingId(null)}>Cancel</Button>
                </div>
              </div>
            ) : (
              <div style={{ display: "flex", gap: "var(--dayvise-space-xs)" }}>
                <Button onClick={() => setConfirmingId(snapshot.snapshotId)} disabled={mutations.restore.isPending}>
                  Restore
                </Button>
                <Button
                  onClick={() => mutations.remove.mutate(snapshot.snapshotId)}
                  disabled={mutations.remove.isPending}
                >
                  Delete
                </Button>
              </div>
            )}
            {mutations.restore.data && mutations.restore.data.snapshotId === snapshot.snapshotId ? (
              <p style={{ ...MUTED_TEXT, margin: 0 }}>
                Restored: {mutations.restore.data.agents} agents and {mutations.restore.data.skills} skills written.
              </p>
            ) : null}
          </div>
        ))}
      </div>
    </Card>
  );
}
